import { Device } from '@psl-ui/neml-device'
import siteConfig from 'src/config/fw-ai/site.config.json'

/**
 * Service responsible for managing device identification and tokenization.
 * Centralizes all logic related to obtaining and converting the device ID,
 * abstracting integration details for clean and reusable usage.
 */
const DeviceIdService = (DougallML, DeviceIdParser) => {
  return {
    /**
     * Initializes the device fingerprinting process and retrieves the unique device ID.
     * Uses configuration from `site.config.json` to customize behavior.
     *
     * @returns {Promise<Object>} A device identification result object containing deviceId and metadata.
     */
    async getDeviceId() {
      const deviceIDConfig = siteConfig?.configuration?.deviceID || {}

      const {
        useCache = true,
        timeout = 10000,
        idk = '',
        tenantId = '',
        serviceId = '',
        ruleset = '',
        rulesetAnon = '',
        onlineIdType = '',
        initOverride = false,
        onlineId = '',
        version = 2
      } = deviceIDConfig

      // Only build config if initOverride is not enabled
      const config = !initOverride && {
        useCache,
        version,
        timeout,
        onlineId,
        skipScript: false,
        profile: {
          idk,
          tenantId,
          serviceId,
          ruleset,
          rulesetAnon,
          onlineIdType
        }
      }

      try {
        const response = await Device.init(config)
        return response
      } catch (error) {
        console.error(
          '[DeviceIdService] Failed to initialize device ID:',
          error
        )
        throw error
      }
    },
    /**
     * Requests a secure Phoenix-compatible token from the backend
     * using the current device fingerprint payload.
     */
    async getSingleDeviceToken(endpoint) {
      try {
        const data = await DeviceIdParser.parseDeviceToTokenPayload()
        const response = await DougallML.fetch(endpoint, 'POST', data)
        return response.data
      } catch (error) {
        console.error('[DeviceIdService] Failed to fetch device token:', error)
        throw error
      }
    }
  }
}

export default DeviceIdService
